// ===== API pública: chaves e escopos (T3.8, RF-PRF-11) =====
//
// A chave de API é de uma empresa e foi criada por um usuário. Escopos = pares `módulo:ação` do RBAC, numa lista
// branca; a cada requisição a permissão efetiva é a interseção dos escopos com as permissões ATUAIS do criador — a chave
// nunca pode mais que ele. O segredo só existe na resposta da criação; guardamos o SHA-256.

import { DomainError } from "./errors";
import { sha256Hex } from "./sha256";

/** Escopos que uma chave pode receber (sem `delete`: exclusão fica na tela). */
export const API_KEY_SCOPES = [
  "contacts:read",
  "contacts:create",
  "contacts:update",
  "products:read",
  "products:create",
  "products:update",
  "products:read_cost",
  "warehouses:read",
  "stock:read",
  "stock:create",
  "statuses:read",
  "sales_orders:read",
  "sales_orders:create",
  "sales_orders:update",
  "purchase_orders:read",
  "purchase_orders:create",
  "purchase_orders:update",
  "invoices:read",
  "receivables:read",
  "receivables:create",
  "receivables:update",
  "payables:read",
  "payables:create",
  "payables:update",
  "dashboard:read",
  "copilot:read",
] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

const SCOPE_SET: ReadonlySet<string> = new Set(API_KEY_SCOPES);
export const isApiKeyScope = (s: string): s is ApiKeyScope => SCOPE_SET.has(s);

/** Permissões no formato do RBAC (`{ módulo: [ações] }`), sem depender do pacote de banco. */
export type PermissionMap = Readonly<Partial<Record<string, readonly string[]>>>;

const split = (s: string) => {
  const i = s.indexOf(":");
  return [s.slice(0, i), s.slice(i + 1)] as const;
};
const has = (p: PermissionMap, s: string) => {
  const [m, a] = split(s);
  return p[m]?.includes(a) ?? false;
};

/** Escopos pedidos que o criador não tem hoje (na ordem pedida). */
export function missingApiKeyScopes(perms: PermissionMap, scopes: readonly string[]): string[] {
  return scopes.filter((s) => !has(perms, s));
}

/** Permissão efetiva da chave: só os escopos da lista branca que o criador ainda tem. */
export function effectiveApiKeyPermissions(
  perms: PermissionMap,
  scopes: readonly string[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const s of scopes) {
    if (!isApiKeyScope(s) || !has(perms, s)) continue;
    const [m, a] = split(s);
    const list = (out[m] ??= []);
    if (!list.includes(a)) list.push(a);
  }
  return out;
}

export const API_KEY_PREFIX = "cxk_";
const BODY_LEN = 40;
/** Caracteres exibidos na lista para reconhecer a chave (`cxk_` + 8). */
export const API_KEY_PREFIX_LEN = 12;
const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const TOKEN_RE = new RegExp(`^${API_KEY_PREFIX}[${B32}]{${BODY_LEN}}$`);

export type RandomFn = (n: number) => Uint8Array;
const cryptoRandom: RandomFn = (n) => crypto.getRandomValues(new Uint8Array(n));

/** Nova chave: `cxk_` + 40 caracteres base32 de Crockford (200 bits). */
export function generateApiKey(random: RandomFn = cryptoRandom): { token: string; prefix: string } {
  const bytes = random(BODY_LEN);
  let body = "";
  for (let i = 0; i < BODY_LEN; i++) body += B32[bytes[i]! % 32];
  const token = API_KEY_PREFIX + body;
  return { token, prefix: apiKeyPrefix(token) };
}

export const isApiKeyToken = (s: string): boolean => TOKEN_RE.test(s);
export const apiKeyPrefix = (token: string): string => token.slice(0, API_KEY_PREFIX_LEN);
/** O token é aleatório de 200 bits: SHA-256 basta (como o token da sessão). */
export const hashApiKey = (token: string): string => sha256Hex(token);

export const MAX_API_KEY_DAYS = 730;
export const DEFAULT_API_KEY_DAYS = 365;
export const MAX_ACTIVE_API_KEYS = 20;

export function apiKeyExpiresAt(now: Date, days: number): string {
  if (!Number.isInteger(days) || days < 1 || days > MAX_API_KEY_DAYS)
    throw new DomainError("validation", `A validade vai de 1 a ${MAX_API_KEY_DAYS} dias.`);
  return new Date(now.getTime() + days * 86_400_000).toISOString();
}

export type ApiKeyStatus = "active" | "revoked" | "expired";
export function apiKeyStatus(k: { revokedAt: string | null; expiresAt: string }, now: Date): ApiKeyStatus {
  if (k.revokedAt) return "revoked";
  return Date.parse(k.expiresAt) <= now.getTime() ? "expired" : "active";
}

const TOUCH_EVERY_MS = 5 * 60_000;
/** "Último uso" é gravado no D1 no máximo a cada 5 minutos por chave. */
export const shouldTouchLastUsed = (lastUsedAt: string | null, now: Date): boolean =>
  !lastUsedAt || now.getTime() - Date.parse(lastUsedAt) >= TOUCH_EVERY_MS;
